"use client";

import { cn, variantBackgroundColors } from "@/lib/utils";
import React, { useMemo, useState } from "react";
import { ButtonStyleType, SpacingType } from "@/types";
import { Button } from "@/components/ui/button";
import { sectionVariants } from "@/types";
import parse from "html-react-parser";
import { SectionWrapper } from "../components/SectionWraper";
import Link from "next/link";
import { Input } from "../ui/input";
import { BorderBeam } from "@/components/magicui/border-beam";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface RateOption {
  label: string;
  conversionRate: number;
  // feePercentage: number;
}

interface PointsCalculatorWithFeesProps {
  darkMode?: boolean;
  variant?: sectionVariants;
  spacing?: SpacingType;
  usedInPageNav?: boolean;
  pageNavTitle?: string;
  themeChangeOnScroll?: boolean;
  title?: string;
  content?: string;
  hasButton?: boolean;
  buttonText?: string;
  buttonLink?: string;
  buttonStyle?: ButtonStyleType;
  microCopy?: string;
  attributes?: React.HTMLProps<HTMLElement>;
  panelTitle?: string;
  panelContent?: string;
  disclaimer?: string;
  conversionRateUnit?: string;
  rateSelectLabel?: string;
  amountLabel?: string;
  // feeLabel?: string;
  rateOptions?: RateOption[];
}

const MAX_AMOUNT_DIGITS = 9;

  // const formatFees = (value: number) =>
  //   `$${value.toLocaleString("en-AU", {
  //     minimumFractionDigits: 2,
  //     maximumFractionDigits: 2,
  //   })}`;

const formatAmountMask = (raw: string) => {
  const cleaned = raw.replace(/[^\d.]/g, "");
  const [integerPart = "", ...decimalParts] = cleaned.split(".");
  const integerDigits = integerPart.replace(/^0+(?=\d)/, "");

  if (integerDigits.length > MAX_AMOUNT_DIGITS) {
    return null;
  }

  const hasDecimal = cleaned.includes(".");
  const decimalDigits = hasDecimal ? decimalParts.join("").slice(0, 2) : "";
  const formattedInteger = integerDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const display = hasDecimal ? `${formattedInteger}.${decimalDigits}` : formattedInteger;
  const numeric = parseFloat(hasDecimal ? `${integerDigits}.${decimalDigits}` : integerDigits) || 0;

  return { display, numeric };
};

const caretPositionAfterMask = (formatted: string, digitsBeforeCaret: number) => {
  if (digitsBeforeCaret <= 0) {
    return formatted.startsWith("0") && formatted.length > 0 ? 1 : 0;
  }

  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i])) {
      seen += 1;
      if (seen === digitsBeforeCaret) {
        return i + 1;
      }
    }
  }

  return formatted.length;
};

const PointsCalculatorWithFees = ({
  darkMode = false,
  variant = "A",
  spacing,
  usedInPageNav = false,
  pageNavTitle = "Points Calculator",
  themeChangeOnScroll = false,
  title,
  content,
  hasButton,
  buttonText,
  buttonLink,
  buttonStyle,
  microCopy,
  attributes,
  panelTitle,
  panelContent,
  disclaimer,
  conversionRateUnit = "points",
  rateSelectLabel = "Rate",
  amountLabel = "(2) Repayment amount",
  rateOptions,
}: PointsCalculatorWithFeesProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [amountDisplay, setAmountDisplay] = useState("");
  const [inputValue, setInputValue] = useState(0);

  const cardBackgroundColor = variantBackgroundColors(darkMode, variant);
  const options = rateOptions ?? [];
  const activeIndex = options.length === 0 ? 0 : Math.min(selectedIndex, options.length - 1);
  const selectedOption = options[activeIndex];

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart } = event.target;
    const digitsBeforeCaret = value
      .slice(0, selectionStart ?? value.length)
      .replace(/\D/g, "").length;
    const formatted = formatAmountMask(value);

    if (!formatted) {
      return;
    }

    setAmountDisplay(formatted.display);
    setInputValue(formatted.numeric);

    requestAnimationFrame(() => {
      const input = document.getElementById("points-amount-input") as HTMLInputElement | null;
      if (!input) {
        return;
      }

      const nextCaret = caretPositionAfterMask(formatted.display, digitsBeforeCaret);
      input.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const { points } = useMemo(() => {
    const conversionRate = selectedOption?.conversionRate || 0;
    // const feePercentage = selectedOption?.feePercentage || 0;

    return {
      points: Math.floor(inputValue * conversionRate).toLocaleString(),
      // fees: formatFees(inputValue * (feePercentage / 100)),
    };
  }, [inputValue, selectedOption]);

  return (
    <SectionWrapper
      usedInPageNav={usedInPageNav}
      pageNavTitle={pageNavTitle}
      themeChangeOnScroll={themeChangeOnScroll}
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={false}
      componentName="points-calculator-with-fees"
      attributes={attributes}
    >
      <div className="container" data-aos="fade-up">
        <div className="mb-8 md:mb-16 flex flex-col text-center">
          {title && (
            <h2 className="max-w-[500px] lg:max-w-[800px] mx-auto">
              {parse(title)}
            </h2>
          )}
          {content && (
            <div className="mt-6 paragraph-lg text-muted-foreground max-w-2xl mx-auto">
              {parse(content)}
            </div>
          )}
          {hasButton && (
            <div>
              <Button asChild variant={buttonStyle} size="lg" className="mt-10 w-full sm:w-auto">
                <Link href={buttonLink ?? ""}>
                  {buttonText}
                </Link>
              </Button>
              {microCopy && (
                <div className="mt-2 paragraph-sm text-muted-foreground text-center">
                  {microCopy}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={cn(cardBackgroundColor, "rounded-2xl relative overflow-hidden")}>
          <div className="flex flex-col px-6 py-10 md:p-10 gap-10">
            <div className="flex flex-col gap-4 text-center md:text-left">
              {panelTitle && (
                <h4>
                  {parse(panelTitle)}
                </h4>
              )}
              {panelContent && (
                <div className="paragraph-lg text-foreground">
                  {parse(panelContent)}
                </div>
              )}
            </div>

            <div className="flex flex-col gap-6 w-full md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-end">
              {options.length > 0 && (
                <div className="flex flex-col gap-2 items-center md:items-start w-full min-w-0">
                  {rateSelectLabel && (
                    <label htmlFor="points-rate-select" className="paragraph-sm text-muted-foreground">
                      {rateSelectLabel}
                    </label>
                  )}
                  <Select
                    value={String(activeIndex)}
                    onValueChange={(value) => setSelectedIndex(Number(value))}
                  >
                    <SelectTrigger id="points-rate-select" className="w-full! min-w-0">
                      <SelectValue placeholder={options[0]?.label} />
                    </SelectTrigger>
                    <SelectContent>
                      {options.map((option, index) => (
                        <SelectItem key={`${option.label}-${index}`} value={String(index)}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex flex-col gap-2 items-center md:items-start w-full min-w-0">
                {amountLabel && (
                  <label htmlFor="points-amount-input" className="paragraph-sm text-muted-foreground">
                    {amountLabel}
                  </label>
                )}
                <div className="relative w-full min-w-0">
                  <span className="material-symbols-outlined absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground !text-[20px]">
                    attach_money
                  </span>
                  <Input
                    id="points-amount-input"
                    className="pl-9"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    placeholder=""
                    value={amountDisplay}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="flex flex-col items-center md:flex-row md:items-end gap-4 md:gap-5 w-full md:w-auto">
                <div className="md:h-[58px] flex items-center justify-center">
                  <span className="material-symbols-outlined text-foreground !text-[20px]">
                    equal
                  </span>
                </div>
                <div className="flex items-center justify-center gap-6 sm:gap-8 md:h-[58px]">
                  <div className="flex flex-col gap-1 items-center md:items-start min-w-[88px] ">
                    <div className="flex items-baseline gap-1"><h3>{points}</h3><span className="paragraph-lg font-bold">{conversionRateUnit}</span></div>
                  </div>

                  {/* <div className="h-12 w-px bg-border" />

                  <div className="flex flex-col gap-1 items-center md:items-start min-w-[88px]">
                    <h6>{fees}</h6>
                    <span className="paragraph-base text-muted-foreground">{feeLabel}</span>
                  </div> */}
                </div>
              </div>
            </div>

            {disclaimer && (
              <div className="paragraph-sm text-muted-foreground text-center md:text-left">
                {parse(disclaimer)}
              </div>
            )}
          </div>
          <BorderBeam duration={8} size={300} />
        </div>
      </div>
    </SectionWrapper>
  );
};

export { PointsCalculatorWithFees };
